import { supabase } from '../lib/supabase';

export interface VideoUploadResult {
  success: boolean;
  videoUrl?: string;
  videoPath?: string;
  videoData?: {
    filename: string;
    size: number;
    mimeType: string;
    duration?: number;
    width?: number;
    height?: number;
  };
  thumbnailUrl?: string;
  thumbnailPath?: string;
  error?: string;
}

export class VideoUploadService {
  private static readonly SUPPORTED_TYPES = [
    'video/mp4',
    'video/webm',
    'video/ogg',
    'video/avi',
    'video/mov',
    'video/wmv',
    'video/flv',
    'video/mkv',
    'video/3gp',
    'video/quicktime'
  ];

  private static readonly MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
  private static readonly MAX_DURATION = 600; // 10 minutes in seconds

  /**
   * Validate if file is a supported video type
   */
  static isSupportedVideoType(file: File): boolean {
    return this.SUPPORTED_TYPES.includes(file.type.toLowerCase());
  }

  /**
   * Get file extension from filename
   */
  static getFileExtension(filename: string): string {
    return filename.split('.').pop()?.toLowerCase() || '';
  }

  /**
   * Get video type category
   */
  static getVideoCategory(mimeType: string): string {
    if (mimeType.includes('mp4')) return 'mp4';
    if (mimeType.includes('webm')) return 'webm';
    if (mimeType.includes('ogg')) return 'ogg';
    if (mimeType.includes('mov') || mimeType.includes('quicktime')) return 'mov';
    if (mimeType.includes('avi')) return 'avi';
    if (mimeType.includes('wmv')) return 'wmv';
    if (mimeType.includes('flv')) return 'flv';
    if (mimeType.includes('mkv')) return 'mkv';
    if (mimeType.includes('3gp')) return '3gp';
    return 'video';
  }

  /**
   * Get video icon based on file type
   */
  static getVideoIcon(mimeType: string): string {
    const category = this.getVideoCategory(mimeType);
    switch (category) {
      case 'mp4': return '🎬';
      case 'webm': return '🎥';
      case 'ogg': return '🎞️';
      case 'mov': return '📹';
      case 'avi': return '🎞️';
      case 'wmv': return '📺';
      case 'flv': return '🎬';
      case 'mkv': return '🎞️';
      case '3gp': return '📱';
      default: return '🎥';
    }
  }

  /**
   * Format file size for display
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Format duration for display
   */
  static formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * Generate video path for storage
   */
  static generateVideoPath(userId: string, messageId: string, filename: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const extension = this.getFileExtension(filename);
    const baseName = filename.replace(/\.[^/.]+$/, '');
    const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_');
    return `videos/${userId}/${timestamp}/${messageId}_${sanitizedBaseName}.${extension}`;
  }

  /**
   * Generate thumbnail path for storage
   */
  static generateThumbnailPath(userId: string, messageId: string, filename: string): string {
    const timestamp = new Date().toISOString().split('T')[0];
    const baseName = filename.replace(/\.[^/.]+$/, '');
    const sanitizedBaseName = baseName.replace(/[^a-zA-Z0-9-_]/g, '_');
    return `videos/${userId}/${timestamp}/${messageId}_${sanitizedBaseName}_thumb.jpg`;
  }

  /**
   * Get video metadata (duration, dimensions)
   */
  static async getVideoMetadata(file: File): Promise<{ duration: number; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      video.onloadedmetadata = () => {
        window.URL.revokeObjectURL(video.src);
        resolve({
          duration: video.duration,
          width: video.videoWidth,
          height: video.videoHeight
        });
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = URL.createObjectURL(file);
    });
  }

  /**
   * Generate video thumbnail
   */
  static async generateThumbnail(file: File, timeOffset: number = 1): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx) {
        reject(new Error('Canvas context not available'));
        return;
      }
      
      video.onloadedmetadata = () => {
        // Set canvas dimensions
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        
        // Seek to the specified time
        video.currentTime = Math.min(timeOffset, video.duration - 0.1);
      };
      
      video.onseeked = () => {
        // Draw the video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        
        // Convert canvas to blob
        canvas.toBlob((blob) => {
          if (blob) {
            window.URL.revokeObjectURL(video.src);
            resolve(blob);
          } else {
            reject(new Error('Failed to generate thumbnail'));
          }
        }, 'image/jpeg', 0.8);
      };
      
      video.onerror = () => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Failed to load video for thumbnail generation'));
      };
      
      video.src = URL.createObjectURL(file);
      video.load();
    });
  }

  /**
   * Upload video to Supabase storage
   */
  static async uploadVideo(
    file: File,
    userId: string,
    messageId: string
  ): Promise<VideoUploadResult> {
    try {
      console.log('Starting video upload process...');

      // Validate file type
      if (!this.isSupportedVideoType(file)) {
        return {
          success: false,
          error: `Unsupported video type: ${file.type}. Supported types: MP4, WebM, OGG, AVI, MOV, WMV, FLV, MKV, 3GP, and QuickTime.`
        };
      }

      // Validate file size
      if (file.size > this.MAX_FILE_SIZE) {
        return {
          success: false,
          error: `Video too large. Maximum size is ${this.formatFileSize(this.MAX_FILE_SIZE)}.`
        };
      }

      // Get video metadata
      let videoMetadata;
      try {
        videoMetadata = await this.getVideoMetadata(file);
        console.log('Video metadata:', videoMetadata);
        
        // Validate duration
        if (videoMetadata.duration > this.MAX_DURATION) {
          return {
            success: false,
            error: `Video too long. Maximum duration is ${this.formatDuration(this.MAX_DURATION)}.`
          };
        }
      } catch (error) {
        console.warn('Failed to get video metadata:', error);
        videoMetadata = { duration: 0, width: 0, height: 0 };
      }

      // Generate video path
      const videoPath = this.generateVideoPath(userId, messageId, file.name);
      console.log('Generated video path:', videoPath);

      // Upload video to Supabase storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('message-videos')
        .upload(videoPath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        console.error('Video upload error:', uploadError);
        
        if (uploadError.message.includes('Bucket not found')) {
          return {
            success: false,
            error: 'Video storage bucket not found. Please contact support.'
          };
        }
        
        return {
          success: false,
          error: `Upload failed: ${uploadError.message}`
        };
      }

      console.log('Video uploaded successfully:', uploadData);

      // Get public URL for video
      const { data: urlData } = supabase.storage
        .from('message-videos')
        .getPublicUrl(videoPath);

      if (!urlData?.publicUrl) {
        return {
          success: false,
          error: 'Failed to generate public URL for video'
        };
      }

      console.log('Video public URL generated:', urlData.publicUrl);

      // Generate and upload thumbnail
      let thumbnailUrl: string | undefined;
      let thumbnailPath: string | undefined;
      
      try {
        const thumbnailBlob = await this.generateThumbnail(file);
        thumbnailPath = this.generateThumbnailPath(userId, messageId, file.name);
        
        const { data: thumbUploadData, error: thumbUploadError } = await supabase.storage
          .from('message-videos')
          .upload(thumbnailPath, thumbnailBlob, {
            cacheControl: '3600',
            upsert: false
          });

        if (!thumbUploadError && thumbUploadData) {
          const { data: thumbUrlData } = supabase.storage
            .from('message-videos')
            .getPublicUrl(thumbnailPath);
          
          thumbnailUrl = thumbUrlData?.publicUrl;
          console.log('Thumbnail uploaded successfully:', thumbnailUrl);
        }
      } catch (error) {
        console.warn('Failed to generate thumbnail:', error);
      }

      return {
        success: true,
        videoUrl: urlData.publicUrl,
        videoPath: videoPath,
        videoData: {
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          duration: Math.round(videoMetadata.duration),
          width: videoMetadata.width,
          height: videoMetadata.height
        },
        thumbnailUrl,
        thumbnailPath
      };

    } catch (error) {
      console.error('Video upload error:', error);
      return {
        success: false,
        error: `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      };
    }
  }

  /**
   * Delete video from storage
   */
  static async deleteVideo(videoPath: string, thumbnailPath?: string): Promise<boolean> {
    try {
      const pathsToDelete = [videoPath];
      if (thumbnailPath) {
        pathsToDelete.push(thumbnailPath);
      }

      const { error } = await supabase.storage
        .from('message-videos')
        .remove(pathsToDelete);

      if (error) {
        console.error('Video deletion error:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Video deletion error:', error);
      return false;
    }
  }

  /**
   * Get supported video extensions
   */
  static getSupportedExtensions(): string[] {
    return ['.mp4', '.webm', '.ogg', '.avi', '.mov', '.wmv', '.flv', '.mkv', '.3gp', '.qt'];
  }

  /**
   * Get video type description
   */
  static getVideoTypeDescription(mimeType: string): string {
    const category = this.getVideoCategory(mimeType);
    switch (category) {
      case 'mp4': return 'MP4 Video';
      case 'webm': return 'WebM Video';
      case 'ogg': return 'OGG Video';
      case 'mov': return 'QuickTime Video';
      case 'avi': return 'AVI Video';
      case 'wmv': return 'Windows Media Video';
      case 'flv': return 'Flash Video';
      case 'mkv': return 'Matroska Video';
      case '3gp': return '3GP Video';
      default: return 'Video File';
    }
  }
}
